import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

const DEFAULT_ROW_PX = 40;

export interface Est3VirtualListProps<T> {
  items: T[];
  rowHeight?: number;
  overscan?: number;
  className?: string;
  renderRow: (item: T, index: number) => React.ReactNode;
}

/**
 * EXTEND UnifiedTasks virtual pattern for EST3 workbench grids (fixed row height).
 */
export function Est3VirtualList<T>({
  items,
  rowHeight = DEFAULT_ROW_PX,
  overscan = 8,
  className,
  renderRow,
}: Est3VirtualListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan,
  });

  return (
    <div
      ref={parentRef}
      className={className}
      style={{ maxHeight: 480, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}
    >
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
        {virtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.key}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: rowHeight,
              transform: `translateY(${virtualItem.start}px)`,
              borderBottom: '1px solid #f5f5f5',
              boxSizing: 'border-box',
            }}
          >
            {renderRow(items[virtualItem.index]!, virtualItem.index)}
          </div>
        ))}
      </div>
    </div>
  );
}
